import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { OverlayProvider, GlobalLoader } from 'seagull';
import { MediaQueriesProvider } from 'seagull/MediaQueries';
import { isTestMode } from 'seagull/utils/test-mode';
import useEventTargets from '~/event-reporter/event-targets/useEventTargets';
import { Observable } from '~/infra/Observable/Observable';
import { BillingObservableProvider } from '~/providers/BillingObservableProvider';
import { RestfulRefreshRegistryProvider } from '~/providers/RestfulRefreshRegistryProvider';
import { queryClient } from '~/queryClient';
import { getAppcuesLoader } from './App.utils';
import { BiFrontendProvider } from './components/BiFrontendProvider/BiFrontendProvider';
import ErrorBoundaryPage from './components/ErrorBoundaryPage';
import { NativeSplashOverlay } from './components/NativeSplashOverlay/NativeSplashOverlay';
import { UpdateRequiredWall } from './components/UpdateRequiredWall/UpdateRequiredWall';
import { useIoContext, useStore } from './contexts';
import { EventReporterProvider } from './event-reporter';
import { useManageLocationStack } from './hooks/useManageLocationStack';
import { useSetAuthToken } from './hooks/useSetAuthToken';
import { getIntentBehavior } from './infra/access-control';
import './infra/styles.scss';
import './infra/tailwind.css';
import { IntercomStarter } from './intercom/IntercomStarter';
import Main from './pages/Main/Main';
import AuthProvider from './providers/AuthProvider';
import { InvoiceChangesObservableProvider } from './providers/InvoiceChangesObservableProvider';
import { ModalCloseManagerProvider } from './providers/ModalCloseManager';
import SeagullIntentBehaviorProvider from './providers/SeagullIntentBehaviorProvider';
import { SeagullMoneyFormatProvider } from './providers/SeagullMoneyFormatProvider';
import { PusherBusinessChannelProvider } from './pusher';
import { selectors } from './store/state-selectors';
import { TasksDrawerStoreProvider } from './tasks-drawer/TasksDrawerStoreProvider';
const ModalWrapper = (props)=>/*#__PURE__*/ _jsx(ErrorBoundaryPage, _object_spread({
        isModal: true
    }, props));
export function App() {
    const { data } = useStore(selectors.ff.defaultFeatureFlags);
    const location = useLocation();
    const setAuthToken = useSetAuthToken();
    const { cookies, native } = useIoContext();
    useManageLocationStack({
        initialLocations: [
            location
        ]
    });
    useEffect(()=>{
        setAuthToken(cookies.getAuthToken());
    }, [
        setAuthToken
    ]);
    const visitorSyncFrame = useMemo(()=>{
        var _document_getElementById;
        return (_document_getElementById = document.getElementById('visitor-sync-frame')) === null || _document_getElementById === void 0 ? void 0 : _document_getElementById.contentWindow;
    }, []);
    const billingObservable = useMemo(()=>new Observable(), []);
    const invoiceChangesObservable = useMemo(()=>new Observable(), []);
    if (native.isNative && native.updateRequired) {
        return /*#__PURE__*/ _jsx(UpdateRequiredWall, {});
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            native.isNative && /*#__PURE__*/ _jsx(NativeSplashOverlay, {}),
            /*#__PURE__*/ _jsx(QueryClientProvider, {
                client: queryClient,
                children: /*#__PURE__*/ _jsx(RestfulRefreshRegistryProvider, {
                    children: /*#__PURE__*/ _jsx(BiFrontendProvider, {
                        children: /*#__PURE__*/ _jsx(SeagullIntentBehaviorProvider, {
                            getIntentBehavior: getIntentBehavior,
                            children: /*#__PURE__*/ _jsx(SeagullMoneyFormatProvider, {
                                children: /*#__PURE__*/ _jsx(ErrorBoundaryPage, {
                                    children: /*#__PURE__*/ _jsx(Suspense, {
                                        fallback: /*#__PURE__*/ _jsx(GlobalLoader, {}),
                                        children: /*#__PURE__*/ _jsx(MediaQueriesProvider, {
                                            children: /*#__PURE__*/ _jsx(AuthProvider, {
                                                loadAppcues: getAppcuesLoader(data),
                                                visitorSyncFrame: visitorSyncFrame,
                                                visitorSyncOrigin: window._visitorSyncHostname || '',
                                                children: /*#__PURE__*/ _jsx(IntercomStarter, {
                                                    children: /*#__PURE__*/ _jsx(PusherBusinessChannelProvider, {
                                                        children: /*#__PURE__*/ _jsx(BillingObservableProvider, {
                                                            observable: billingObservable,
                                                            children: /*#__PURE__*/ _jsx(InvoiceChangesObservableProvider, {
                                                                observable: invoiceChangesObservable,
                                                                children: /*#__PURE__*/ _jsx(OverlayProvider, {
                                                                    disableEnforceFocus: true,
                                                                    ModalWrapper: ModalWrapper,
                                                                    isTestMode: isTestMode,
                                                                    children: /*#__PURE__*/ _jsx(TasksDrawerStoreProvider, {
                                                                        children: /*#__PURE__*/ _jsx(ModalCloseManagerProvider, {
                                                                            children: /*#__PURE__*/ _jsx(Main, {})
                                                                        })
                                                                    })
                                                                })
                                                            })
                                                        })
                                                    })
                                                })
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        ]
    });
}
export function AppWithEventReporter() {
    return /*#__PURE__*/ _jsx(EventReporterProvider, {
        eventTargets: useEventTargets(),
        children: /*#__PURE__*/ _jsx(App, {})
    });
}
